import { useState, useRef, useEffect } from "react";
import { Box, CircularProgress, Grid, IconButton, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { SchemaFieldItem } from "../../types";

type TreeNode = { name: string; fullKey: string; children: TreeNode[] };

function buildTree(items: SchemaFieldItem[]): TreeNode[] {
  const root: TreeNode[] = [];
  const map = new Map<string, TreeNode>();
  for (const item of items) {
    const parts = item.key.split(".");
    let level = root;
    for (let i = 0; i < parts.length; i++) {
      const key = parts.slice(0, i + 1).join(".");
      let node = map.get(key);
      if (!node) { node = { name: parts[i], fullKey: key, children: [] }; map.set(key, node); level.push(node); }
      level = node.children;
    }
  }
  return root;
}

function findNode(tree: TreeNode[], key: string | null): TreeNode | null {
  if (!key) return null;
  for (const n of tree) {
    if (n.fullKey === key) return n;
    const f = findNode(n.children, key);
    if (f) return f;
  }
  return null;
}

const LINE = "#c8c8c8";
const INDENT = 24;
const CONNECTOR = 14;
const ROW_H = 22;

const wrapperSx = (isLast: boolean) => ({
  position: "relative" as const,
  pl: `${CONNECTOR}px`,
  "&::before": {
    content: '""',
    position: "absolute",
    left: 0,
    top: 0,
    ...(isLast ? { height: ROW_H / 2 } : { bottom: 0 }),
    width: "1px",
    backgroundColor: LINE,
  },
  "&::after": {
    content: '""',
    position: "absolute",
    left: 0,
    top: ROW_H / 2,
    width: `${CONNECTOR}px`,
    height: "1px",
    backgroundColor: LINE,
  },
});

type TreeRowProps = {
  node: TreeNode;
  depth: number;
  sel: boolean;
  clickable: boolean;
  defaultOpen: boolean;
  onClick: () => void;
  renderChild: (child: TreeNode, depth: number) => React.ReactNode;
};

const TreeRow: React.FC<TreeRowProps> = ({ node, depth, sel, clickable, defaultOpen, onClick, renderChild }) => {
  const [open, setOpen] = useState(defaultOpen);
  const has = node.children.length > 0;
  const rowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (sel && rowRef.current) {
      rowRef.current.scrollIntoView({ block: "nearest", inline: "nearest" });
    }
  }, [sel]);

  return (
    <Box mb={depth === 0 ? "6px" : 0}>
      <Box
        ref={rowRef}
        onClick={clickable ? onClick : undefined}
        sx={{
          display: "flex",
          alignItems: "center",
          cursor: clickable ? "pointer" : "default",
          minHeight: ROW_H,
          minWidth: "max-content",
          px: 0.5,
          py: "2px",
          borderRadius: "4px",
          position: "relative",
          flexWrap: "nowrap",
          backgroundColor: sel ? "var(--color-primary-alpha-07)" : "transparent",
          border: sel ? "1px solid var(--color-primary)" : "1px solid transparent",
          "&:hover": { backgroundColor: sel ? "var(--color-primary-alpha-07)" : clickable ? "var(--color-primary-alpha-07)" : "transparent" },
        }}
      >
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); setOpen(p => !p); }}
          sx={{ visibility: has ? "visible" : "hidden", width: 22, height: 22, flexShrink: 0, color: sel ? "var(--color-primary)" : "inherit" }}
        >
          {open ? <ExpandMoreIcon sx={{ fontSize: 15 }} /> : <ChevronRightIcon sx={{ fontSize: 15 }} />}
        </IconButton>
        <Typography variant="body2" sx={{ fontFamily: "monospace", color: sel ? "var(--color-primary)" : "text.primary", ml: 0.25, whiteSpace: "nowrap" }}>
          {node.name}
        </Typography>
        {has && <Typography variant="body2" sx={{ color: sel ? "var(--color-primary)" : "text.secondary", ml: 0.25, whiteSpace: "nowrap", flexShrink: 0 }}>{"{ }"}</Typography>}
      </Box>

      {open && has && (
        <Box sx={{ ml: `${INDENT}px` }}>
          {node.children.map((child, idx) => (
            <Box key={child.fullKey} sx={wrapperSx(idx === node.children.length - 1)}>
              {renderChild(child, depth + 1)}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

type LeftRowProps = {
  node: TreeNode;
  depth: number;
  selectedKey: string | null;
  onSelect: (key: string) => void;
};

const LeftRow: React.FC<LeftRowProps> = ({ node, depth, selectedKey, onSelect }) => (
  <TreeRow
    node={node}
    depth={depth}
    sel={node.fullKey === selectedKey}
    clickable
    defaultOpen={false}
    onClick={() => onSelect(node.fullKey)}
    renderChild={(child, d) => (
      <LeftRow node={child} depth={d} selectedKey={selectedKey} onSelect={onSelect} />
    )}
  />
);

type RightRowProps = {
  node: TreeNode;
  depth: number;
  schemaItems: SchemaFieldItem[];
  onToggle: (key: string, val: boolean) => void;
};

const RightRow: React.FC<RightRowProps> = ({ node, depth, schemaItems, onToggle }) => {
  const item = schemaItems.find(x => x.key === node.fullKey);
  const sel = item?.isDeviceId ?? false;
  return (
    <TreeRow
      node={node}
      depth={depth}
      sel={sel}
      clickable={!!item}
      defaultOpen
      onClick={() => item && onToggle(node.fullKey, !sel)}
      renderChild={(child, d) => (
        <RightRow node={child} depth={d} schemaItems={schemaItems} onToggle={onToggle} />
      )}
    />
  );
};

const Panel = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Box sx={{ border: "1px solid rgba(0,0,0,0.09)", borderRadius: 1, overflow: "auto", maxHeight: "50rem", backgroundColor: "#fff" }}>
    <Box sx={{ position: "sticky", top: 0, zIndex: 1, backgroundColor: "#fff", borderBottom: "1px solid rgba(0,0,0,0.09)", px: 2, py: 1, fontSize: 13, fontWeight: 600, color: "#374151" }}>
      {title}
    </Box>
    <Box sx={{ p: 1 }}>
      {children}
    </Box>
  </Box>
);

type PayloadTreeViewProps = {
  schemaItems: SchemaFieldItem[];
  setSchemaItems: React.Dispatch<React.SetStateAction<SchemaFieldItem[]>>;
  loading: boolean;
};

const PayloadTreeView: React.FC<PayloadTreeViewProps> = ({ schemaItems, setSchemaItems, loading }) => {
  const tree = buildTree(schemaItems);
  const arrayKey = schemaItems.find(x => x.isArray)?.key ?? null;
  const arrayNode = findNode(tree, arrayKey);

  const handleSelectArray = (key: string) => {
    const cur = schemaItems.find(x => x.key === key)?.isArray ?? false;
    const next = !cur;
    setSchemaItems(prev => prev.map(x => {
      if (x.key === key) return { ...x, isArray: next, isDeviceId: false };
      const child = next && x.key.startsWith(key + ".");
      return { ...x, isArray: false, isDeviceId: child ? x.isDeviceId : false };
    }));
  };

  const handleDeviceId = (key: string, value: boolean) => {
    setSchemaItems(prev => prev.map(x => x.key === key ? { ...x, isDeviceId: value } : { ...x, isDeviceId: false }));
  };

  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", pt: 4 }}><CircularProgress size={32} /></Box>;
  if (tree.length === 0) return <Box sx={{ pt: 2 }}><Typography variant="body2" color="text.secondary" align="center">Veri Bulunamadı</Typography></Box>;

  return (
    <Grid container spacing={2}>
      <Grid size={6}>
        <Panel title="Device Payload Route">
          {tree.map(node => (
            <LeftRow key={node.fullKey} node={node} depth={0} selectedKey={arrayKey} onSelect={handleSelectArray} />
          ))}
        </Panel>
      </Grid>

      <Grid size={6}>
        <Panel title={`Sensor ID Route${arrayKey ? ` - ${arrayKey}` : ""}`}>
          {arrayNode && arrayNode.children.length > 0 ? (
            arrayNode.children.map(child => (
              <RightRow key={child.fullKey} node={child} depth={0} schemaItems={schemaItems} onToggle={handleDeviceId} />
            ))
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
              Sol taraftan bir array seçin
            </Typography>
          )}
        </Panel>
      </Grid>
    </Grid>
  );
};

export default PayloadTreeView;
